import { ApolloError } from '@apollo/client';

export const MEMBER_DETAILS_ERROR_MESSAGES = {
  unauthorized: 'Unauthorized request. Please log in and try again.',
  forbidden: 'Forbidden request. Insufficient permissions to load this data.',
  generic: 'Data could not be loaded. Please try again later.',
  noData: 'No data found.',
} as const;

const AUTHENTICATION_ERROR_CODES = ['AUTH_NOT_AUTHENTICATED'];
const AUTHORIZATION_ERROR_CODES = ['AUTH_NOT_AUTHORIZED', 'FORBIDDEN'];

const toStatus = (value: unknown): number | undefined => {
  const status = Number(value);
  return Number.isInteger(status) && status > 0 ? status : undefined;
};

/**
 * Resolves the HTTP-like status of a failed query.
 * Looks at the network error first (HTTP 401/403 responses), then at GraphQL
 * errors (explicit status in `extensions`, or well-known auth error codes).
 */
export const getQueryErrorStatus = (
  error?: ApolloError | null,
): number | undefined => {
  if (!error) return undefined;

  const networkStatus = toStatus(
    (error.networkError as { statusCode?: number } | null)?.statusCode,
  );
  if (networkStatus) return networkStatus;

  for (const graphQLError of error.graphQLErrors ?? []) {
    const extensions = graphQLError.extensions as
      | { code?: unknown; status?: unknown; statusCode?: unknown }
      | undefined;
    const status = toStatus(extensions?.status ?? extensions?.statusCode);
    if (status) return status;

    const code = String(extensions?.code ?? '').toUpperCase();
    if (AUTHENTICATION_ERROR_CODES.includes(code)) return 401;
    if (AUTHORIZATION_ERROR_CODES.includes(code)) return 403;
  }

  return undefined;
};

/**
 * Maps a failed query to a user-facing message:
 * 401 -> unauthorized, 403 -> forbidden, anything else (other 4xx, 5xx,
 * GraphQL execution errors, unknown) -> generic.
 */
export const getMemberDetailsErrorMessage = (
  error?: ApolloError | null,
): string => {
  switch (getQueryErrorStatus(error)) {
    case 401:
      return MEMBER_DETAILS_ERROR_MESSAGES.unauthorized;
    case 403:
      return MEMBER_DETAILS_ERROR_MESSAGES.forbidden;
    default:
      return MEMBER_DETAILS_ERROR_MESSAGES.generic;
  }
};
